/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { isNounKey, type NounKeys } from '../../../../../common/constants/nouns';
import { is1pProductKey, ProductKeys } from '../../../../../common/constants/product-keys';
import { ProductOrNounIcon } from '../../../../../common/ui/product-icon';

const ICON_SIZE_PX = '20px';

// RovoLogoFromGlyph/RovoLogoFromSVG (constants/icons/index.tsx and its rovo-connectors-config
// copy) clamp size="xsmall" to a 16px box via a `correctedSize` table, regardless of what the
// underlying glyph is. Every 3P/Forge icon is built from that wrapper except this handful, which
// instead call RovoLogoFromLogo directly (the same wrapper 1P icons use) and so already render
// at a correct 20px for "xsmall". Keep this in sync with which configs call RovoLogoFromLogo in
// rovo-connectors-config/src/common/ui/configs/*/index.tsx.
const NATIVE_TWENTY_PX_PRODUCTS: ReadonlySet<ProductKeys> = new Set([
	ProductKeys.Loom,
	ProductKeys.ConfluenceDC,
	ProductKeys.JiraDC,
	ProductKeys.Gitbook,
	ProductKeys.AWSS3,
	ProductKeys.JsmDC,
	ProductKeys.AtlassianWebCrawler,
]);

const ALREADY_TWENTY_PX_SCALE = '1';
const SIXTEEN_TO_TWENTY_PX_SCALE = String(20 / 16);

const boxStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: ICON_SIZE_PX,
	height: ICON_SIZE_PX,
	overflow: 'hidden',
	flexShrink: 0,
});

const contentStyles = css({
	display: 'inline-flex',
	transform: 'scale(var(--facepile-icon-scale))',
});

export type FacepileIconProps = {
	label?: string;
	productOrNounKey: ProductKeys | NounKeys | undefined;
};

// Noun icons bypass RovoLogo entirely (constants/product-icon renders them via their own
// @atlaskit/icon component directly), so they are left unscaled — this facepile only shows
// apps in practice, but nouns are covered here defensively without changing their behaviour.
const isAlreadyTwentyPx = (key: ProductKeys | NounKeys | undefined): boolean =>
	key === undefined ||
	isNounKey(key) ||
	is1pProductKey(key) ||
	NATIVE_TWENTY_PX_PRODUCTS.has(key as ProductKeys);

// AppStack passes size="xsmall" to every item. RovoLogoFromGlyph/RovoLogoFromSVG only render
// that correctly at 20px behind rovo-search-team-eu-26-brand-icon-library (see their own fix);
// with the gate off, everything but the products above still renders at a fixed 16px, corrected
// here with a static scale since that 16px is a hardcoded constant, not something that varies
// at runtime.
export const FacepileIcon = ({ label, productOrNounKey }: FacepileIconProps): React.JSX.Element => {
	const isAlreadyCorrectSize =
		fg('rovo-search-team-eu-26-brand-icon-library') || isAlreadyTwentyPx(productOrNounKey);

	return (
		<span css={boxStyles}>
			<span
				css={contentStyles}
				style={
					{
						'--facepile-icon-scale': isAlreadyCorrectSize
							? ALREADY_TWENTY_PX_SCALE
							: SIXTEEN_TO_TWENTY_PX_SCALE,
					} as React.CSSProperties
				}
			>
				<ProductOrNounIcon label={label} size="xsmall" productOrNounKey={productOrNounKey} />
			</span>
		</span>
	);
};
